import type { ReadingMode } from '@lilith-arcana-os/domain';

export type SpreadIntent =
  | 'daily'
  | 'clarity'
  | 'timeline'
  | 'decision'
  | 'relationship'
  | 'shadow'
  | 'healing'
  | 'spiritual'
  | 'yearly'
  | 'lilith'
  | 'kabbalistic'
  | 'open';

export type PositionRoleId =
  | 'message'
  | 'past'
  | 'present'
  | 'future'
  | 'situation'
  | 'action'
  | 'outcome'
  | 'obstacle'
  | 'advice'
  | 'self'
  | 'other'
  | 'bond'
  | 'challenge'
  | 'resource'
  | 'option_a'
  | 'option_b'
  | 'option_c'
  | 'consequence_a'
  | 'consequence_b'
  | 'hidden'
  | 'shadow'
  | 'desire'
  | 'wound'
  | 'temptation'
  | 'repressed_power'
  | 'chain'
  | 'truth'
  | 'ritual_action'
  | 'integration'
  | 'month_1'
  | 'month_2'
  | 'month_3'
  | 'month_4'
  | 'month_5'
  | 'month_6'
  | 'month_7'
  | 'month_8'
  | 'month_9'
  | 'month_10'
  | 'month_11'
  | 'month_12'
  | 'significator'
  | 'mask'
  | 'cost';

export interface PositionRole {
  id: PositionRoleId;
  label: string;
  description: string;
  intent_tags: SpreadIntent[];
  temporal_axis: 'past' | 'present' | 'future' | 'timeless' | null;
  relational_axis: 'self' | 'other' | 'between' | null;
  shadow_axis: 'surface' | 'hidden' | 'descent' | 'integration' | null;
  recommended_modes: ReadingMode[];
}

const ALL_MODES: ReadingMode[] = ['standard', 'lilith', 'qliphotic', 'kabbalistic'];
const SHADOW_MODES: ReadingMode[] = ['standard', 'lilith', 'qliphotic'];

const defineRole = (role: PositionRole): PositionRole => role;

const MONTHLY_ROLES: PositionRole[] = Array.from({ length: 12 }, (_, index) =>
  defineRole({
    id: `month_${index + 1}` as PositionRoleId,
    label: `Mese ${index + 1}`,
    description: `Corrente dominante del mese ${index + 1} del ciclo annuale.`,
    intent_tags: ['yearly', 'timeline'],
    temporal_axis: 'future',
    relational_axis: null,
    shadow_axis: null,
    recommended_modes: ['standard', 'lilith', 'kabbalistic'],
  }),
);

export const POSITION_ROLE_REGISTRY: PositionRole[] = [
  defineRole({
    id: 'message',
    label: 'Messaggio',
    description: 'Segnale essenziale che sintetizza la corrente della lettura.',
    intent_tags: ['daily', 'clarity', 'open'],
    temporal_axis: 'timeless',
    relational_axis: null,
    shadow_axis: null,
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'past',
    label: 'Passato',
    description: 'Fattore o memoria che continua a condizionare il presente.',
    intent_tags: ['timeline', 'clarity', 'healing'],
    temporal_axis: 'past',
    relational_axis: null,
    shadow_axis: null,
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'present',
    label: 'Presente',
    description: 'Nucleo attivo della situazione in questo momento.',
    intent_tags: ['clarity', 'daily', 'open'],
    temporal_axis: 'present',
    relational_axis: null,
    shadow_axis: null,
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'future',
    label: 'Futuro',
    description: 'Direzione probabile se la corrente attuale resta invariata.',
    intent_tags: ['timeline', 'yearly', 'decision'],
    temporal_axis: 'future',
    relational_axis: null,
    shadow_axis: null,
    recommended_modes: ['standard', 'lilith', 'kabbalistic'],
  }),
  defineRole({
    id: 'situation',
    label: 'Situazione',
    description: 'Campo reale in cui la domanda prende forma.',
    intent_tags: ['clarity', 'decision', 'relationship', 'open'],
    temporal_axis: 'present',
    relational_axis: null,
    shadow_axis: 'surface',
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'action',
    label: 'Azione',
    description: 'Movimento consigliato per intervenire nella situazione.',
    intent_tags: ['clarity', 'decision', 'healing'],
    temporal_axis: 'present',
    relational_axis: null,
    shadow_axis: null,
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'outcome',
    label: 'Esito',
    description: 'Esito verosimile o stato risultante del processo in corso.',
    intent_tags: ['clarity', 'decision', 'timeline', 'relationship'],
    temporal_axis: 'future',
    relational_axis: null,
    shadow_axis: 'integration',
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'obstacle',
    label: 'Ostacolo',
    description: 'Freno, attrito o interferenza che limita il movimento.',
    intent_tags: ['clarity', 'decision', 'relationship', 'shadow'],
    temporal_axis: 'present',
    relational_axis: null,
    shadow_axis: 'hidden',
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'advice',
    label: 'Consiglio',
    description: 'Riallineamento pratico o simbolico suggerito dalla lettura.',
    intent_tags: ['clarity', 'decision', 'healing', 'spiritual'],
    temporal_axis: 'present',
    relational_axis: null,
    shadow_axis: 'integration',
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'self',
    label: 'Sé',
    description: 'Polarità del consultante o punto di vista interno.',
    intent_tags: ['relationship', 'healing', 'shadow'],
    temporal_axis: 'present',
    relational_axis: 'self',
    shadow_axis: 'surface',
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'other',
    label: 'Altro',
    description: 'Polarità dell’altra persona o del polo esterno.',
    intent_tags: ['relationship', 'decision'],
    temporal_axis: 'present',
    relational_axis: 'other',
    shadow_axis: 'surface',
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'bond',
    label: 'Legame',
    description: 'Campo di scambio, attrazione o tensione tra due poli.',
    intent_tags: ['relationship', 'healing'],
    temporal_axis: 'present',
    relational_axis: 'between',
    shadow_axis: null,
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'challenge',
    label: 'Sfida',
    description: 'Prova specifica che richiede disciplina, chiarezza o coraggio.',
    intent_tags: ['decision', 'shadow', 'spiritual', 'relationship'],
    temporal_axis: 'present',
    relational_axis: null,
    shadow_axis: 'descent',
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'resource',
    label: 'Risorsa',
    description: 'Forza disponibile, sostegno latente o alleato da attivare.',
    intent_tags: ['decision', 'healing', 'relationship', 'spiritual'],
    temporal_axis: 'present',
    relational_axis: null,
    shadow_axis: 'integration',
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'option_a',
    label: 'Opzione A',
    description: 'Prima via praticabile messa a confronto nella lettura.',
    intent_tags: ['decision'],
    temporal_axis: 'future',
    relational_axis: null,
    shadow_axis: null,
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'option_b',
    label: 'Opzione B',
    description: 'Seconda via praticabile messa a confronto nella lettura.',
    intent_tags: ['decision'],
    temporal_axis: 'future',
    relational_axis: null,
    shadow_axis: null,
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'option_c',
    label: 'Opzione C',
    description: 'Terza via possibile quando la scelta non è binaria.',
    intent_tags: ['decision'],
    temporal_axis: 'future',
    relational_axis: null,
    shadow_axis: null,
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'consequence_a',
    label: 'Conseguenza A',
    description: 'Effetto probabile della prima traiettoria o scelta.',
    intent_tags: ['decision', 'timeline'],
    temporal_axis: 'future',
    relational_axis: null,
    shadow_axis: null,
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'consequence_b',
    label: 'Conseguenza B',
    description: 'Effetto probabile della seconda traiettoria o scelta.',
    intent_tags: ['decision', 'timeline'],
    temporal_axis: 'future',
    relational_axis: null,
    shadow_axis: null,
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'hidden',
    label: 'Nascosto',
    description: 'Fattore sotterraneo che non è ancora visibile in pieno.',
    intent_tags: ['shadow', 'clarity', 'relationship'],
    temporal_axis: 'timeless',
    relational_axis: null,
    shadow_axis: 'hidden',
    recommended_modes: SHADOW_MODES,
  }),
  defineRole({
    id: 'shadow',
    label: 'Ombra',
    description: 'Aspetto rimosso o negato che chiede riconoscimento.',
    intent_tags: ['shadow', 'healing', 'lilith'],
    temporal_axis: 'timeless',
    relational_axis: null,
    shadow_axis: 'descent',
    recommended_modes: SHADOW_MODES,
  }),
  defineRole({
    id: 'desire',
    label: 'Desiderio',
    description: 'Impulso autentico o fame simbolica che muove la lettura.',
    intent_tags: ['lilith', 'relationship', 'shadow'],
    temporal_axis: 'timeless',
    relational_axis: null,
    shadow_axis: 'hidden',
    recommended_modes: ['standard', 'lilith'],
  }),
  defineRole({
    id: 'wound',
    label: 'Ferita',
    description: 'Lesione originaria o vulnerabilità che riapre il tema.',
    intent_tags: ['shadow', 'healing', 'lilith'],
    temporal_axis: 'past',
    relational_axis: null,
    shadow_axis: 'descent',
    recommended_modes: SHADOW_MODES,
  }),
  defineRole({
    id: 'temptation',
    label: 'Richiamo',
    description: 'Attrazione ambigua che seduce, devia o mette alla prova.',
    intent_tags: ['lilith', 'decision', 'relationship'],
    temporal_axis: 'present',
    relational_axis: null,
    shadow_axis: 'hidden',
    recommended_modes: ['standard', 'lilith', 'qliphotic'],
  }),
  defineRole({
    id: 'repressed_power',
    label: 'Potere represso',
    description: 'Energia negata che, se integrata, diventa forza operativa.',
    intent_tags: ['shadow', 'healing', 'lilith', 'spiritual'],
    temporal_axis: 'timeless',
    relational_axis: null,
    shadow_axis: 'descent',
    recommended_modes: ['standard', 'lilith', 'qliphotic'],
  }),
  defineRole({
    id: 'chain',
    label: 'Catena',
    description: 'Schema ripetitivo, dipendenza o vincolo da spezzare.',
    intent_tags: ['shadow', 'healing', 'lilith'],
    temporal_axis: 'past',
    relational_axis: null,
    shadow_axis: 'descent',
    recommended_modes: SHADOW_MODES,
  }),
  defineRole({
    id: 'truth',
    label: 'Verità',
    description: 'Centro non negoziabile della lettura, anche se scomodo.',
    intent_tags: ['clarity', 'shadow', 'lilith', 'spiritual'],
    temporal_axis: 'present',
    relational_axis: null,
    shadow_axis: 'integration',
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'ritual_action',
    label: 'Azione rituale',
    description: 'Passo concreto o gesto simbolico che chiude e attiva la lettura.',
    intent_tags: ['lilith', 'spiritual', 'healing', 'daily'],
    temporal_axis: 'future',
    relational_axis: null,
    shadow_axis: 'integration',
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'integration',
    label: 'Integrazione',
    description: 'Esito archetipico dell’assimilazione del processo.',
    intent_tags: ['shadow', 'healing', 'lilith', 'spiritual'],
    temporal_axis: 'future',
    relational_axis: null,
    shadow_axis: 'integration',
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'significator',
    label: 'Significatore',
    description: 'Carta asse che stabilisce tono, soggetto e cornice del ciclo.',
    intent_tags: ['yearly', 'open', 'kabbalistic'],
    temporal_axis: 'present',
    relational_axis: null,
    shadow_axis: null,
    recommended_modes: ALL_MODES,
  }),
  defineRole({
    id: 'mask',
    label: 'Maschera',
    description: 'Facciata adattiva, personaggio sociale o postura visibile.',
    intent_tags: ['lilith', 'relationship', 'shadow'],
    temporal_axis: 'present',
    relational_axis: 'self',
    shadow_axis: 'surface',
    recommended_modes: ['standard', 'lilith', 'qliphotic'],
  }),
  defineRole({
    id: 'cost',
    label: 'Costo',
    description: 'Prezzo simbolico, energetico o concreto richiesto dalla scelta.',
    intent_tags: ['decision', 'timeline'],
    temporal_axis: 'future',
    relational_axis: null,
    shadow_axis: null,
    recommended_modes: ALL_MODES,
  }),
  ...MONTHLY_ROLES,
];

export const POSITION_ROLE_MAP: Record<PositionRoleId, PositionRole> = Object.fromEntries(
  POSITION_ROLE_REGISTRY.map((role) => [role.id, role]),
) as Record<PositionRoleId, PositionRole>;

export const getPositionRole = (roleId: PositionRoleId): PositionRole => POSITION_ROLE_MAP[roleId];
